// @tennis-matches/services/scoring
// Tournament scoring and statistics computation

import {
  normalizeResultRules,
  normalizeSets,
  outcomeFromSets,
  sumGames,
} from "@tennis-matches/core/utils";

/**
 * Parst scoreText (z. B. "4:3", "6:4, 3:6") in sets-Array für recomputeStats.
 * Bei Korrekturen wird oft nur scoreText gespeichert; die Rangliste braucht sets.
 * @param {string} scoreText - z. B. "4:3" oder "6:4, 3:6"
 * @returns {Array<{ a: number, b: number }>} sets oder [] wenn ungültig
 */
function setsFromScoreText(scoreText) {
  let s = typeof scoreText === "string" ? scoreText.trim() : "";
  if (!s) return [];
  s = s.replace(/^Satz\s+/i, "").trim();
  const parts = s.split(/[,;]/).map((p) => p.replace(/\bMTB\s*/i, "").trim());
  const sets = [];
  for (const part of parts) {
    const match = part.match(/^(\d+)\s*:\s*(\d+)$/);
    if (!match) continue;
    const a = parseInt(match[1], 10);
    const b = parseInt(match[2], 10);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      sets.push({ a, b });
    }
  }
  return sets;
}

/**
 * Recomputes player statistics from schedule and results
 * @param {Object} params
 * @param {Array} params.schedule - Tournament schedule (array of rounds)
 * @param {Object} params.results - Match results by matchKey
 * @param {Object} params.points - Points configuration { win, draw }
 * @param {number} params.playerCount - Total number of players
 * @param {Object} params.resultRules - Result rules configuration
 * @param {number} params.currentRound - Current round number (default: 1)
 * @param {number} params.completedRound - Last completed round (default: 0)
 * @returns {Object} Statistics map by player ID
 */
export function recomputeStats({
  schedule,
  results,
  points,
  playerCount,
  resultRules,
  currentRound = 1,
  completedRound = 0,
}) {
  const pc = Number(playerCount) || 0;
  const pts = points || { win: 2, draw: 1 };
  const rr = normalizeResultRules(resultRules);

  const map = {};
  for (let i = 1; i <= pc; i++) map[String(i)] = emptyRow(i);

  const rounds = Array.isArray(schedule) ? schedule : [];

  for (let r = 0; r < rounds.length; r++) {
    const roundNo = r + 1;

    // ✅ Runde gilt als "abgeschlossen", wenn sie vor der aktuellen liegt ODER explizit abgeschlossen wurde
    const roundIsClosed = roundNo < Number(currentRound) || roundNo <= Number(completedRound);

    const matches = Array.isArray(rounds[r]) ? rounds[r] : [];
    for (let mIdx = 0; mIdx < matches.length; mIdx++) {
      const m = matches[mIdx];
      if (!m) continue;

      // DB schedules may omit `type`. Infer from teams.
      let typ = String(m.type || "").toLowerCase().trim();
      const A0 = Array.isArray(m.A) ? m.A : [];
      const B0 = Array.isArray(m.B) ? m.B : [];
      const hasTeams = A0.length > 0 && B0.length > 0;

      if (typ !== "singles" && typ !== "doubles") {
        if (!hasTeams) continue;
        typ = (A0.length === 1 && B0.length === 1) ? "singles" : "doubles";
      }

      // WICHTIG: id kann aus m.id kommen ODER aus roundNo/mIdx generiert werden
      // Aber: m.id sollte bereits der richtige Key sein (r1m1, r2m1, etc.)
      const id = m.id || `r${roundNo}m${mIdx + 1}`;
      const res = (results || {})[id] || {};
      // Bei Korrekturen bleibt oft veraltetes res.sets erhalten; scoreText ist dann die Wahrheit → scoreText vorziehen
      const fromScoreText = typeof res.scoreText === "string" && res.scoreText.trim()
        ? setsFromScoreText(res.scoreText)
        : [];
      const rawSets = fromScoreText.length > 0
        ? fromScoreText
        : (Array.isArray(res.sets) && res.sets.length > 0 ? res.sets : []);
      const sets = normalizeSets(rawSets.length > 0 ? rawSets : res.sets);

      // ✅ outcome abhängig vom Modus (ONE_SET_OPEN: erst nach Rundenabschluss entscheiden)
      const outcome = outcomeFromSets(sets, rr, { roundIsClosed });

      if (!outcome) {
        continue;
      }

      const teamA = Array.isArray(m.A) ? m.A.map(String) : [];
      const teamB = Array.isArray(m.B) ? m.B.map(String) : [];

      const { aGames, bGames } = sumGames(sets);

      applyPlayed(map, teamA, teamB, aGames, bGames);

      if (outcome === "D") {
        applyDraw(map, teamA, teamB, pts.draw);
      } else if (outcome === "A") {
        applyWinLoss(map, teamA, teamB, pts.win);
      } else if (outcome === "B") {
        applyWinLoss(map, teamB, teamA, pts.win);
      }
    }
  }

  for (let i = 1; i <= pc; i++) {
    const row = map[String(i)];
    row.GD = row.GF - row.GA;
    row.avgGamesPerMatch = row.played > 0 ? round1((row.GF + row.GA) / row.played) : null;
  }

  return map;
}

/* -------------------------
   Stats helpers
------------------------- */

function emptyRow(i) {
  return {
    id: String(i),
    points: 0,
    played: 0,
    W: 0,
    D: 0,
    L: 0,
    GF: 0,
    GA: 0,
    GD: 0,
    avgGamesPerMatch: null,
  };
}

function applyPlayed(map, teamA, teamB, aGames, bGames) {
  for (const p of teamA) {
    if (!map[p]) continue;
    map[p].played += 1;
    map[p].GF += aGames;
    map[p].GA += bGames;
  }
  for (const p of teamB) {
    if (!map[p]) continue;
    map[p].played += 1;
    map[p].GF += bGames;
    map[p].GA += aGames;
  }
}

function applyDraw(map, teamA, teamB, drawPts) {
  for (const p of teamA) {
    if (!map[p]) continue;
    map[p].D += 1;
    map[p].points += drawPts;
  }
  for (const p of teamB) {
    if (!map[p]) continue;
    map[p].D += 1;
    map[p].points += drawPts;
  }
}

function applyWinLoss(map, winners, losers, winPts) {
  for (const p of winners) {
    if (!map[p]) continue;
    map[p].W += 1;
    map[p].points += winPts;
  }
  for (const p of losers) {
    if (!map[p]) continue;
    map[p].L += 1;
  }
}

function round1(x) {
  return Math.round(x * 10) / 10;
}
